import moment from 'moment';

import { EmploymentDetailsProposalProps } from '~/data/proposals';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import { ScheduleEmploymentDetailFormValues } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useScheduleEmploymentDetail';
import {
  EmployeeProposalDecision,
  mapSubmitDataUtils,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { isRoleHR } from '~/services/Permissions';

export type EmploymentDetailPostData = {
  fEmplDetailCalcHoursBalance: string;
  fEmplDetailIsPending: boolean;
  fEmplDetailJobGrade: string;
  fEmplDetailPersonnelGroup1: string;
  fEmplDetailPersonnelGroup2: string;
  fEmplDetailPersonnelGroup3: string;
  fEmplDetailPersonnelGroup4: string;
  fEmplDetailPersonnelGroup5: string;
  fEmplDetailStartDate: string;
  fEmplDetailStatus: string;
  fEmplDetailTaxNumber: string;
  fEmplDetailVacBalCalcSixDays: number;
  fEmplId: number | undefined;
  fEmplUid: string;
  fEmplDetailCustomData: unknown;
};

export type EmploymentDetailProposalSubmitData = {
  fEmplDetailProposalStartDate: string;
  fEmplDetailProposalCalcHoursBalance: boolean;
  fEmplDetailProposalJobGrade: string;
  fEmplDetailProposalPersonnelGroup1: string;
  fEmplDetailProposalPersonnelGroup2: string;
  fEmplDetailProposalPersonnelGroup3: string;
  fEmplDetailProposalPersonnelGroup4: string;
  fEmplDetailProposalPersonnelGroup5: string;
  fEmplDetailProposalEmplStatus: string;
  fEmplDetailProposalTaxNumber: string;
  fEmplDetailProposalVacBalCalcSixDays: number;
  fEmplDetailProposalManagerId: number;
  fEmplDetailProposalHigherMgrId: number;
  fEmplDetailProposalHrId: number;
  fEmplDetailProposalStatus: string;
  fEmplSubUid?: string;
  fEmplDetailProposalCustomData?: unknown;
};

export const mapEmploymentDetailsPostData = (
  data: ScheduleEmploymentDetailFormValues,
  fEmplId: number | undefined,
  fEmplUid: string,
): EmploymentDetailPostData => ({
  fEmplDetailCalcHoursBalance: (!!data.fEmplDetailCalcHoursBalance).toString(),
  fEmplDetailIsPending: true,
  fEmplDetailJobGrade: data.fEmplDetailJobGrade,
  fEmplDetailPersonnelGroup1: data.fEmplDetailPersonnelGroup1 || '',
  fEmplDetailPersonnelGroup2: data.fEmplDetailPersonnelGroup2 || '',
  fEmplDetailPersonnelGroup3: data.fEmplDetailPersonnelGroup3 || '',
  fEmplDetailPersonnelGroup4: data.fEmplDetailPersonnelGroup4 || '',
  fEmplDetailPersonnelGroup5: data.fEmplDetailPersonnelGroup5 || '',
  fEmplDetailStartDate: moment(
    data.fEmplDetailStartDate,
    scheduledDateFormats.dotFormat,
  ).format(scheduledDateFormats.dashFormat),
  fEmplDetailStatus: data.fEmplDetailStatus,
  fEmplDetailTaxNumber: data.fEmplDetailTaxNumber,
  fEmplDetailVacBalCalcSixDays: data.fEmplDetailVacBalCalcSixDays,
  fEmplId: fEmplId,
  fEmplUid: fEmplUid,
  fEmplDetailCustomData: data.customData,
});

export const mapEmploymentProposalDetailsPostData = (
  data: ScheduleEmploymentDetailFormValues,
  loggedInUserRoles: string[],
): EmploymentDetailProposalSubmitData => {
  const {
    submissionResponsibleHR,
    submissionDateInFormat,
    submissionProposalStatus,
  } = mapSubmitDataUtils({
    defaultSubmissionDateFormat: scheduledDateFormats.dashFormat,
  });
  const isHRUser = isRoleHR(loggedInUserRoles);

  return {
    fEmplDetailProposalCalcHoursBalance: Boolean(
      data.fEmplDetailCalcHoursBalance,
    ),
    fEmplDetailProposalJobGrade: data.fEmplDetailJobGrade,
    fEmplDetailProposalPersonnelGroup1: data.fEmplDetailPersonnelGroup1 || '',
    fEmplDetailProposalPersonnelGroup2: data.fEmplDetailPersonnelGroup2 || '',
    fEmplDetailProposalPersonnelGroup3: data.fEmplDetailPersonnelGroup3 || '',
    fEmplDetailProposalPersonnelGroup4: data.fEmplDetailPersonnelGroup4 || '',
    fEmplDetailProposalPersonnelGroup5: data.fEmplDetailPersonnelGroup5 || '',
    fEmplDetailProposalEmplStatus: data.fEmplDetailStatus,
    fEmplDetailProposalTaxNumber: data.fEmplDetailTaxNumber,
    fEmplDetailProposalVacBalCalcSixDays: data.fEmplDetailVacBalCalcSixDays,
    fEmplDetailProposalStartDate: submissionDateInFormat(
      data.fEmplDetailStartDate,
    ),
    fEmplDetailProposalManagerId: -1,
    fEmplDetailProposalHigherMgrId: -1,
    fEmplDetailProposalHrId: submissionResponsibleHR(
      data.fEmplDetailProposalHrId,
    ),
    fEmplDetailProposalStatus: submissionProposalStatus({
      isHRUser,
      decision: undefined,
    }),
    fEmplDetailProposalCustomData: data.customData,
  };
};

export const mapEmploymentProposalDetailsPutData = (
  proposal: EmploymentDetailsProposalProps,
  loggedInUserRoles: string[],
  decision?: EmployeeProposalDecision,
): EmploymentDetailProposalSubmitData => {
  const {
    submissionResponsibleHR,
    submissionDateInFormat,
    submissionProposalStatus,
  } = mapSubmitDataUtils({
    defaultSubmissionDateFormat: scheduledDateFormats.dashFormat,
  });
  const isHRUser = isRoleHR(loggedInUserRoles);
  const {
    proposalData: { ed: data, customData },
  } = proposal;

  return {
    fEmplDetailProposalCalcHoursBalance: data.calc_hours_balance,
    fEmplDetailProposalJobGrade: data.job_grade_fk,
    fEmplDetailProposalPersonnelGroup1: data.personnel_group_1_fk || '',
    fEmplDetailProposalPersonnelGroup2: data.personnel_group_2_fk || '',
    fEmplDetailProposalPersonnelGroup3: data.personnel_group_3_fk || '',
    fEmplDetailProposalPersonnelGroup4: data.personnel_group_4_fk || '',
    fEmplDetailProposalPersonnelGroup5: data.personnel_group_5_fk || '',
    fEmplDetailProposalEmplStatus: data.employment_status_fk,
    fEmplDetailProposalTaxNumber: data.tax_number || '',
    fEmplDetailProposalVacBalCalcSixDays: data.vac_bal_calc_use_six_days,
    fEmplDetailProposalStartDate: submissionDateInFormat(data.start_date),
    fEmplDetailProposalManagerId: -1,
    fEmplDetailProposalHigherMgrId: -1,
    fEmplDetailProposalHrId: submissionResponsibleHR(data.workflow_props.hrFK),
    fEmplDetailProposalStatus: submissionProposalStatus({ isHRUser, decision }),
    fEmplSubUid: proposal.proposalId,
    fEmplDetailProposalCustomData: customData,
  };
};
